// Import API service
import { usersApi } from '@/services/api'

// Initial state
const state = {
  users: [],
  roles: [],
  loading: false,
  error: null
}

// Getters
const getters = {
  allUsers: state => state.users,
  allRoles: state => state.roles,
  isLoading: state => state.loading,
  error: state => state.error
}

// Mutations
const mutations = {
  SET_USERS(state, users) {
    state.users = users
  },
  SET_ROLES(state, roles) {
    state.roles = roles
  },
  SET_LOADING(state, status) {
    state.loading = status
  },
  SET_ERROR(state, error) {
    state.error = error
  }
}

// Actions
const actions = {
  async fetchUsers({ commit }) {
    commit('SET_LOADING', true)
    try {
      const response = await usersApi.getUsers()
      commit('SET_USERS', response.data)
      commit('SET_ERROR', null)
    } catch (error) {
      console.error('Error fetching users:', error)
      commit('SET_ERROR', 'Failed to load users')
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async fetchRoles({ commit }) {
    commit('SET_LOADING', true)
    try {
      const response = await usersApi.getRoles()
      commit('SET_ROLES', response.data)
      commit('SET_ERROR', null)
    } catch (error) {
      console.error('Error fetching roles:', error)
      commit('SET_ERROR', 'Failed to load roles')
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async createUser({ commit, dispatch }, userData) {
    commit('SET_LOADING', true)
    try {
      await usersApi.createUser(userData)
      commit('SET_ERROR', null)
      await dispatch('fetchUsers') // Refresh the users list
      return { success: true, message: 'User created successfully' }
    } catch (error) {
      console.error('Error creating user:', error)
      commit('SET_ERROR', error.response?.data?.message || 'Failed to create user')
      return { success: false, message: error.response?.data?.message || 'Failed to create user' }
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async updateUser({ commit, dispatch }, { id, userData }) {
    commit('SET_LOADING', true)
    try {
      await usersApi.updateUser(id, userData)
      commit('SET_ERROR', null)
      await dispatch('fetchUsers') // Refresh the users list
      return { success: true, message: 'User updated successfully' }
    } catch (error) {
      console.error('Error updating user:', error)
      commit('SET_ERROR', error.response?.data?.message || 'Failed to update user')
      return { success: false, message: error.response?.data?.message || 'Failed to update user' }
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async deleteUser({ commit, dispatch }, id) {
    commit('SET_LOADING', true)
    try {
      await usersApi.deleteUser(id)
      commit('SET_ERROR', null)
      await dispatch('fetchUsers') // Refresh the users list
      return { success: true, message: 'User deleted successfully' }
    } catch (error) {
      console.error('Error deleting user:', error)
      commit('SET_ERROR', error.response?.data?.message || 'Failed to delete user')
      return { success: false, message: error.response?.data?.message || 'Failed to delete user' }
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async resetPassword({ commit }, { id, password }) {
    commit('SET_LOADING', true)
    try {
      await usersApi.adminChangePassword(id, password)
      commit('SET_ERROR', null)
      return { success: true, message: 'Password reset successfully' }
    } catch (error) {
      console.error('Error resetting password:', error)
      commit('SET_ERROR', error.response?.data?.message || 'Failed to reset password')
      return { success: false, message: error.response?.data?.message || 'Failed to reset password' }
    } finally {
      commit('SET_LOADING', false)
    }
  } 
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
} 